import React, { useState, useEffect } from 'react';
import PlantCard from './PlantCard';
import medicinalPlantsService from '../../services/medicinalPlantsService';

/**
 * MedicinalPlantsExplorer Component
 * Main explorer page for browsing Mexican medicinal plants
 * Based on UNAM's Atlas de las Plantas de la Medicina Tradicional Mexicana
 */
const MedicinalPlantsExplorer = ({ onPlantClick }) => {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // En teléfono los filtros arrancan plegados; en escritorio, abiertos
  // (ahí el espacio sobra y verlos de golpe ayuda).
  const [filtersOpen, setFiltersOpen] = useState(
    typeof window === 'undefined' ? true : window.innerWidth > 600
  );

  // Filters state
  const [filters, setFilters] = useState({
    category: '',
    evidenceLevel: '',
    safetyLevel: '',
    search: '',
    page: 1,
    pageSize: 20
  });

  // Fetch plants on mount and when filters change
  useEffect(() => {
    fetchPlants();
  }, [filters]);

  const fetchPlants = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await medicinalPlantsService.getPlants(filters);
      // The API's PlantListResponse keys the list as `plants` (not `items`);
      // falling back to the raw object rendered the eternal empty state.
      setPlants(data.plants || data.items || (Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Error fetching medicinal plants:', err);
      setError('Error al cargar las plantas medicinales. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      handleFilterChange('search', value);
    }, 500);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      evidenceLevel: '',
      safetyLevel: '',
      search: '',
      page: 1,
      pageSize: 20
    });
  };

  const activeFilterCount = [filters.category, filters.evidenceLevel, filters.safetyLevel]
    .filter(Boolean).length;

  // Get filter options from service
  const categories = medicinalPlantsService.getCategories();
  const evidenceLevels = medicinalPlantsService.getEvidenceLevels();
  const safetyLevels = medicinalPlantsService.getSafetyLevels();

  if (loading && plants.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Cargando plantas medicinales...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>
            <LeafIcon />
            Plantas Medicinales Mexicanas
          </h1>
          <p style={styles.subtitle}>
            Conocimiento ancestral mexicano, con respaldo del Atlas de la UNAM
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div style={styles.searchSection}>
        <div style={styles.searchContainer}>
          <span style={styles.searchIcon} aria-hidden><SearchIcon /></span>
          <input
            type="search"
            inputMode="search"
            aria-label="Buscar planta medicinal"
            placeholder="Buscar planta o padecimiento…"
            style={styles.searchInput}
            onChange={handleSearchChange}
            defaultValue={filters.search}
          />
        </div>
      </div>

      {/* Filters Section — plegados en el teléfono: tres desplegables
          abiertos empujaban las plantas 700px hacia abajo, y la mayoría de
          la gente entra a ver plantas, no a filtrar. */}
      <div style={styles.filtersSection}>
        <div style={styles.filtersHeader}>
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            style={styles.filtersToggle}
          >
            <FilterIcon />
            Filtros
            {activeFilterCount > 0 && (
              <span style={styles.filterCount}>{activeFilterCount}</span>
            )}
            <span aria-hidden style={{ ...styles.chevron, transform: filtersOpen ? 'rotate(180deg)' : 'none' }}>
              <ChevronIcon />
            </span>
          </button>
          {activeFilterCount > 0 && (
            <button style={styles.clearButton} onClick={clearFilters}>
              Limpiar
            </button>
          )}
        </div>

        <div style={{ ...styles.filtersGrid, display: filtersOpen ? styles.filtersGrid.display : 'none' }}>
          {/* Category Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Categoría:</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              style={styles.select}
            >
              <option value="">Todas las categorías</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Evidence Level Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Nivel de Evidencia:</label>
            <select
              value={filters.evidenceLevel}
              onChange={(e) => handleFilterChange('evidenceLevel', e.target.value)}
              style={styles.select}
            >
              <option value="">Todos los niveles</option>
              {evidenceLevels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          {/* Safety Level Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Nivel de Seguridad:</label>
            <select
              value={filters.safetyLevel}
              onChange={(e) => handleFilterChange('safetyLevel', e.target.value)}
              style={styles.select}
            >
              <option value="">Todos los niveles</option>
              {safetyLevels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div style={styles.resultsSection}>
        <div style={styles.resultsHeader}>
          <h3 style={styles.resultsTitle}>
            {plants.length > 0 ? (
              <>Encontradas {plants.length} plantas</>
            ) : (
              <>No se encontraron plantas</>
            )}
          </h3>
        </div>

        {error && (
          <div style={styles.errorMessage}>
            <span style={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        {plants.length === 0 && !loading && !error && (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>🌿</span>
            <h3 style={styles.emptyTitle}>No se encontraron plantas</h3>
            <p style={styles.emptyText}>
              Intenta ajustar los filtros o realizar una búsqueda diferente
            </p>
          </div>
        )}

        {plants.length > 0 && (
          <div style={styles.plantsGrid}>
            {plants.map(plant => (
              <PlantCard
                key={plant.id}
                plant={plant}
                onClick={onPlantClick}
              />
            ))}
          </div>
        )}

        {loading && plants.length > 0 && (
          <div style={styles.loadingMore}>
            Cargando más plantas...
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div style={styles.infoBanner}>
        <div style={styles.infoBannerContent}>
          <span style={styles.infoBannerIcon}>ℹ️</span>
          <div>
            <strong>Importante:</strong> La información presentada es de carácter
            informativo y educativo. Siempre consulta con un profesional de la salud
            antes de usar cualquier planta medicinal, especialmente si estás embarazada,
            amamantando o tomando medicamentos.
          </div>
        </div>
      </div>
    </div>
  );
};

/* Íconos SVG en línea — nunca emojis (se ven distintos en cada teléfono y
   no heredan el color de la marca). */
const LeafIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden
       style={{ flex: '0 0 auto' }}>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 5h18M6 12h12M10 19h4" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    // Móvil primero: 12px de aire lateral, no 20 (a 390px cada píxel cuenta).
    padding: 'clamp(12px, 3vw, 20px)',
    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
    backgroundColor: '#F4EBD9',
    minHeight: '100vh',
    boxSizing: 'border-box',
    // Sin esto, el padding del buscador se sumaba a su width:100% y toda la
    // pantalla se desplazaba de lado en el teléfono.
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #28a745',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    color: '#666',
    fontSize: '16px',
  },
  // El encabezado ocupaba 750px de una pantalla de 844: había que hacer
  // scroll a ciegas para ver la primera planta. Ahora es una banda compacta
  // alineada a la izquierda (se lee más rápido que centrada).
  header: {
    textAlign: 'left',
    marginBottom: 'clamp(14px, 3vw, 30px)',
    padding: 'clamp(16px, 4vw, 40px) clamp(16px, 4vw, 24px)',
    background: 'linear-gradient(135deg, #1F5C3D 0%, #2E8B57 100%)',
    borderRadius: '16px',
    color: 'white',
  },
  headerContent: {
    maxWidth: '800px',
    margin: 0,
  },
  title: {
    fontFamily: "'Fraunces', serif",
    margin: '0 0 6px 0',
    fontSize: 'clamp(1.35rem, 5.5vw, 2.25rem)',
    fontWeight: '700',
    lineHeight: 1.15,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  subtitle: {
    margin: 0,
    fontSize: 'clamp(0.9rem, 3.4vw, 1.125rem)',
    fontWeight: '400',
    opacity: 0.95,
    lineHeight: '1.5',
  },
  searchSection: {
    marginBottom: '25px',
  },
  searchContainer: {
    position: 'relative',
    maxWidth: '800px',
    margin: '0 auto',
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    color: '#4C5142',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box', // ← el desbordamiento lateral vivía aquí
    padding: '13px 14px 13px 44px',
    // 16px es el piso: por debajo, iOS Safari hace zoom al enfocar y deja la
    // app ampliada (regla dura de DESIGN.md).
    fontSize: '16px',
    minHeight: '48px',
    border: '2px solid #D8C29A',
    borderRadius: '12px',
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: '#FBF5E9',
    color: '#20251C',
  },
  filtersSection: {
    backgroundColor: '#ECDDC4',
    padding: 'clamp(14px, 3.5vw, 25px)',
    boxSizing: 'border-box',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
    marginBottom: '30px',
  },
  filtersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  filtersToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '44px',
    padding: '8px 4px',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    font: 'inherit',
    fontSize: '17px',
    fontWeight: 600,
    color: '#20251C',
  },
  filterCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '22px',
    height: '22px',
    borderRadius: '999px',
    backgroundColor: '#E0892E',
    color: '#16201A',
    fontSize: '12.5px',
    fontWeight: 700,
  },
  chevron: {
    display: 'inline-flex',
    color: '#4C5142',
    transition: 'transform .18s ease',
  },
  filtersTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#2c3e50',
  },
  clearButton: {
    padding: '8px 14px',
    minHeight: '44px',
    backgroundColor: 'transparent',
    color: '#B8531F',
    border: '1px solid #D8C29A',
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s',
  },
  filtersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
    gap: 'clamp(12px, 3vw, 20px)',
    marginTop: '12px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
  },
  select: {
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  resultsSection: {
    marginBottom: '30px',
  },
  resultsHeader: {
    marginBottom: '25px',
  },
  resultsTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    color: '#2c3e50',
  },
  errorMessage: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    padding: '15px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#856404',
    marginBottom: '20px',
  },
  errorIcon: {
    fontSize: '24px',
  },
  emptyState: {
    textAlign: 'center',
    padding: 'clamp(24px, 6vw, 60px) clamp(12px, 3vw, 20px)',
    backgroundColor: '#ECDDC4',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.3,
  },
  emptyTitle: {
    margin: '20px 0 10px 0',
    fontSize: '24px',
    color: '#495057',
  },
  emptyText: {
    margin: 0,
    fontSize: '16px',
    color: '#6c757d',
  },
  plantsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
    gap: '25px',
  },
  loadingMore: {
    textAlign: 'center',
    padding: '20px',
    color: '#666',
    fontSize: '14px',
  },
  infoBanner: {
    backgroundColor: '#e7f3ff',
    border: '1px solid #b3d9ff',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '30px',
  },
  infoBannerContent: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-start',
    fontSize: '14px',
    color: '#004085',
    lineHeight: '1.6',
  },
  infoBannerIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
};

export default MedicinalPlantsExplorer;
